import { useQueryClient } from '@tanstack/react-query';
import { BadgeCheck } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Badge, Button, Field, Input, useToast } from '@/components/ui';
import { useSession } from '@/features/auth/session-context';
import { usePatientActions } from '@/features/patients/api';
import type { Coverage, PatientDetail } from '@/features/patients/types';
import { TZ } from '@/pages/calendar/time';
import { isApiError } from '@/lib/api';
import styles from './PatientCoverages.module.css';

const checkedOn = (iso: string) =>
  new Date(iso).toLocaleString('en-GB', {
    timeZone: TZ,
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });

/**
 * RL-1119: a patient's live memberships with their source (entered at the
 * counter or by the patient in the app) and check status. A membership typed
 * in the app is not used as a payer until the counter has seen the card and
 * marked it checked (`POST /patients/:id/coverages/:coverageId/check`).
 */
export function PatientCoverages({
  patient,
}: {
  patient: Pick<PatientDetail, 'id' | 'status' | 'coverages'>;
}) {
  const { hasPermission } = useSession();
  const canCheck =
    hasPermission('patients:update') && patient.status !== 'merged';
  if (!patient.coverages.length) return <>Self</>;
  return (
    <ul className={styles.list} aria-label="Memberships">
      {patient.coverages.map((c) => (
        <CoverageItem
          key={c.id}
          patientId={patient.id}
          coverage={c}
          canCheck={canCheck}
        />
      ))}
    </ul>
  );
}

function CoverageItem({
  patientId,
  coverage: c,
  canCheck,
}: {
  patientId: string;
  coverage: Coverage;
  canCheck: boolean;
}) {
  const [checking, setChecking] = useState(false);
  const pending = c.check === 'pending_check';
  return (
    <li className={styles.item}>
      <span className={styles.title}>
        {c.payer.name} <span className={styles.member}>{c.memberNo}</span>
      </span>
      <span className={styles.badges}>
        <Badge tone={c.source === 'app' ? 'info' : 'neutral'}>
          {c.source === 'app' ? 'Entered in the app' : 'Entered at the counter'}
        </Badge>
        <Badge tone={pending ? 'warning' : 'success'} dot>
          {pending ? 'Not checked yet' : 'Checked'}
        </Badge>
        {c.source === 'app' && c.checkedAt && (
          <span className={styles.meta}>
            Card checked {checkedOn(c.checkedAt)}
          </span>
        )}
      </span>
      {pending && (
        <span className={styles.meta}>
          Not used as a payer until the card is checked at the counter.
        </span>
      )}
      {pending &&
        canCheck &&
        (checking ? (
          <CheckForm
            patientId={patientId}
            coverage={c}
            onDone={() => setChecking(false)}
          />
        ) : (
          <span>
            <Button
              size="sm"
              icon={<BadgeCheck size={16} />}
              onClick={() => setChecking(true)}
              aria-label={`Mark ${c.payer.name} ${c.memberNo} as checked`}
            >
              Mark as checked
            </Button>
          </span>
        ))}
    </li>
  );
}

function CheckForm({
  patientId,
  coverage: c,
  onDone,
}: {
  patientId: string;
  coverage: Coverage;
  onDone: () => void;
}) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const toast = useToast();
  const { checkCoverage } = usePatientActions();
  const [memberNo, setMemberNo] = useState('');
  const [fieldError, setFieldError] = useState<string>();
  const [failure, setFailure] = useState<{
    message: string;
    requestId?: string;
  } | null>(null);
  const corrected = memberNo.trim();
  const tooShort = corrected.length === 1;

  async function confirm() {
    setFailure(null);
    setFieldError(undefined);
    try {
      await checkCoverage.mutateAsync({
        id: patientId,
        coverageId: c.id,
        memberNo: corrected && corrected !== c.memberNo ? corrected : undefined,
      });
      toast.show({ tone: 'success', title: 'Membership checked' });
      onDone();
    } catch (err) {
      if (!isApiError(err)) {
        setFailure({ message: 'Could not mark the membership as checked.' });
        return;
      }
      setFieldError(err.fieldError('memberNo'));
      setFailure({
        message:
          err.code === 'COVERAGE_NOT_FOUND'
            ? 'This membership is no longer waiting for a check (it was checked or ended). The record has been refreshed.'
            : err.message,
        requestId: err.requestId,
      });
      if (err.code === 'COVERAGE_NOT_FOUND') {
        void qc.invalidateQueries({ queryKey: ['patient', patientId] });
        void qc.invalidateQueries({ queryKey: ['patient-record', patientId] });
      }
    }
  }

  return (
    <div
      className={styles.checkForm}
      role="group"
      aria-label={`Check ${c.payer.name} card`}
    >
      <span className={styles.meta}>
        Compare the card with what the patient entered: {c.payer.name}{' '}
        {c.memberNo}.
      </span>
      <Field
        label="Member number on the card (if different)"
        hint="Leave blank if the card matches."
        error={tooShort ? 'At least 2 characters' : fieldError}
      >
        <Input
          value={memberNo}
          maxLength={40}
          onChange={(e) => setMemberNo(e.target.value)}
          onKeyDown={(e) => {
            // The patient drawer is a form: Enter here confirms the check only.
            if (e.key !== 'Enter') return;
            e.preventDefault();
            if (!tooShort && !checkCoverage.isPending) void confirm();
          }}
        />
      </Field>
      {failure && (
        <p className={styles.error} role="alert">
          {failure.message}
          {failure.requestId && (
            <span>{t('common.requestId', { id: failure.requestId })}</span>
          )}
        </p>
      )}
      <span className={styles.checkActions}>
        <Button
          size="sm"
          variant="primary"
          loading={checkCoverage.isPending}
          disabled={tooShort}
          onClick={() => void confirm()}
        >
          Card checked
        </Button>
        <Button size="sm" onClick={onDone}>
          Cancel
        </Button>
      </span>
    </div>
  );
}
